import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Facebook, Instagram, ShieldCheck } from "lucide-react";

export const RERA_AGENT = "UPRERAAGT15084";

const CITIES = ["Lucknow", "Gurugram", "Noida", "Delhi", "Mumbai", "Bangalore", "Pune", "Hyderabad", "Dubai", "Singapore"];

const SOCIAL = [
  { Icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/oc-infraventures", testId: "social-linkedin" },
  { Icon: Facebook, label: "Facebook", href: "https://www.facebook.com/ocinfraventures", testId: "social-facebook" },
  { Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/ocinfraventures", testId: "social-instagram" },
];

export default function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-white mt-24" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <img src="/oc-logo.png" alt="OC Infraventures" className="h-14 w-auto object-contain" />
            </div>
            <p className="text-slate-600 text-sm leading-relaxed max-w-sm">
              Founded in 2015, OC Infraventures is a one-stop solution for all your real estate needs —
              from buying and selling to home loans, legal consultation and NRI services.
            </p>

            <div
              data-testid="footer-rera-agent"
              className="mt-5 inline-flex items-start gap-3 border border-[#D4A437]/30 bg-amber-50 px-4 py-3 rounded-lg"
            >
              <ShieldCheck className="w-4 h-4 text-[#B88C2E] mt-0.5 shrink-0" strokeWidth={2} />
              <div className="text-xs leading-relaxed">
                <div className="overline text-[#B88C2E] mb-1">UP RERA Agent</div>
                <div className="text-slate-900 font-mono tracking-wider font-semibold">{RERA_AGENT}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              {SOCIAL.map(({ Icon, label, href, testId }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={testId}
                  aria-label={label}
                  className="w-10 h-10 rounded-lg border border-slate-200 bg-white hover:border-[#D4A437] hover:text-[#D4A437] hover:bg-amber-50 text-slate-600 flex items-center justify-center transition-all"
                >
                  <Icon className="w-4 h-4" strokeWidth={2} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="overline mb-4">Explore</div>
            <ul className="space-y-2.5 text-sm text-slate-600">
              <li><Link to="/projects" className="hover:text-[#D4A437] transition-colors">All Projects</Link></li>
              <li><Link to="/hot-deals" className="text-[#EF4444] hover:text-[#DC2626] font-semibold transition-colors">Hot Deals</Link></li>
              <li><Link to="/compare" className="hover:text-[#D4A437] transition-colors">Compare</Link></li>
              <li><Link to="/insights" className="hover:text-[#D4A437] transition-colors">Price Insights</Link></li>
              <li><Link to="/valuation" className="hover:text-[#D4A437] transition-colors">Property Valuation</Link></li>
              <li><Link to="/vastu" className="hover:text-[#D4A437] transition-colors">Vastu Calculator</Link></li>
              <li><Link to="/emi-calculator" className="hover:text-[#D4A437] transition-colors">EMI Calculator</Link></li>
              <li><Link to="/post-property" className="hover:text-[#D4A437] transition-colors">Post Property</Link></li>
              <li><Link to="/home-loans" className="hover:text-[#D4A437] transition-colors">Home Loans</Link></li>
              <li><Link to="/interior-works" className="hover:text-[#D4A437] transition-colors">Interior Works</Link></li>
              <li><Link to="/nri-services" className="hover:text-[#D4A437] transition-colors">NRI Services</Link></li>
              <li><Link to="/about" className="hover:text-[#D4A437] transition-colors">About Us</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="overline mb-4">Cities</div>
            <ul className="grid grid-cols-2 gap-y-2.5 text-sm text-slate-600">
              {CITIES.map((c) => (
                <li key={c}><Link to={`/city/${c}`} className="hover:text-[#D4A437] transition-colors">{c}</Link></li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="overline mb-4">Contact</div>
            <ul className="space-y-3 text-sm text-slate-600">
              <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-0.5 text-[#D4A437] shrink-0" strokeWidth={2} /> info@ocinfraventures.com</li>
              <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-0.5 text-[#D4A437] shrink-0" strokeWidth={2} /> +91 522 4000 800</li>
              <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-[#D4A437] shrink-0" strokeWidth={2} />
                <span className="leading-relaxed">504B Felix Square,<br/>International Business Bay,<br/>Sushant Golf City, Lucknow 226030</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-200 mt-12 pt-6 flex flex-col md:flex-row justify-between gap-4 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} OC Infraventures &amp; Constructions Pvt Ltd. All rights reserved.</div>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-[#D4A437] transition-colors">Privacy Policy</Link>
            <span className="text-slate-300">·</span>
            <span data-testid="footer-rera-line">UP RERA Agent · {RERA_AGENT}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
